import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ContentReviewDisplay } from "../content-editing/components/ContentReviewDisplay";
import type { Submission as ProviderSubmission } from "../content-editing/hooks/useSubmissions";
import { ApprovalDialog } from "./components/ApprovalDialog";
import { RejectionDialog } from "./components/RejectionDialog";
import { AdminActionsPanel } from "./components/ReviewSubmissionComponents";
import { useReviewWorkflow } from "./hooks/useReviewWorkflow";
import { useSubmission } from "./hooks/useSubmissions";
import { useTriggerEncoding } from "./hooks/useTriggerEncoding";
import { LoadingSpinner } from "../../shared/components/loading/LoadingSpinner";

/**
 * Admin Review Submission Page
 * Shows full submission details with video player and approval/rejection controls
 * Fetches real submission data from API
 */
// eslint-disable-next-line max-lines-per-function
export function ReviewSubmissionPage() {
  const { id } = useParams<{ id: string }>();
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Fetch submission data from API
  const { submission, loading, error, refetch } = useSubmission(id || "");

  // Review workflow state and handlers
  const {
    showApprovalDialog,
    showRejectionDialog,
    approvalLoading,
    rejectionLoading,
    handleApprove,
    handleApprovalConfirm,
    handleApprovalCancel,
    handleReject,
    handleRejectionConfirm,
    handleRejectionCancel,
  } = useReviewWorkflow(id);

  // Publish hook (triggers encoding and auto-publishing)
  const { triggerEncoding, loading: encodingLoading } = useTriggerEncoding(id);

  /**
   * Handle publish button click
   */
  const handleTriggerEncoding = () => {
    void (async () => {
      const result = await triggerEncoding();
      if (result) {
        alert(
          `Publishing initiated! ${result.assetsProcessed} video(s) are being encoded and will be published automatically when ready.`,
        );
        // Refresh to show updated encoding status
        refetch();
      } else {
        alert("Failed to start publishing. Check console for details.");
      }
    })();
  };

  /**
   * Handle manual refresh of submission data to check encoding status
   */
  const handleRefresh = () => {
    setIsRefreshing(true);
    refetch();
    // Clear refreshing state after a brief delay
    setTimeout(() => {
      setIsRefreshing(false);
    }, 1000);
  };

  /**
   * Auto-refresh when encoding is in progress
   * Polls every 10 seconds if any asset has status 'ENCODING'
   */
  useEffect(() => {
    if (!submission?.assets) {
      return;
    }

    // Check if any assets are currently encoding
    const hasEncodingAssets = submission.assets.some((asset) => asset.encodingStatus === "ENCODING");

    if (!hasEncodingAssets) {
      return;
    }

    // Set up polling interval
    const pollInterval = setInterval(() => {
      refetch();
    }, 10000); // Poll every 10 seconds

    // Cleanup interval on unmount or when encoding completes
    return () => {
      clearInterval(pollInterval);
    };
  }, [submission?.assets, refetch]);

  if (loading) {
    return (
      <div className="mx-auto py-8 px-4">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !submission) {
    return (
      <div className="mx-auto py-8 px-4">
        <div className="text-center text-red-400">{error ? `Error: ${error}` : "Submission not found"}</div>
        <div className="text-center mt-4">
          <Link to="/admin/review-queue" className="text-lumi-accent hover:underline">
            ← Back to Review Queue
          </Link>
        </div>
      </div>
    );
  }

  // Adapt admin submission type to provider submission type for compatibility
  // with existing display components (different nullability in slug field)
  const adaptedSubmission: ProviderSubmission = {
    ...submission,
    slug: submission.slug || `submission-${submission.id}`, // Provide default slug if null
    status: submission.status as ProviderSubmission["status"], // Cast status string to enum
    languages: submission.languages || [], // Ensure languages array exists
    horizontalTileUrl: submission.horizontalTileUrl || null,
    verticalTileUrl: submission.verticalTileUrl || null,
    assets: submission.assets?.map((asset) => ({
      ...asset,
      rawFileSize: asset.rawFileSize || "0", // Provide default if null
      encodingCost: "0.00", // Default value for compatibility
      dashUrl: asset.dashUrl ?? null, // Include DASH URL
      streamVideoId: asset.streamVideoId ?? null, // Include Stream video ID
    })),
  };

  return (
    <div className="mx-auto py-8">
      {/* Header */}
      <div className="mb-6">
        <Link to="/admin/review-queue" className="text-neutral-400 hover:text-white mb-4 inline-block text-sm">
          ← Back to Review Queue
        </Link>
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white">{adaptedSubmission.title}</h1>
            <p className="text-neutral-400 mt-1">Review content submission for approval</p>
          </div>
        </div>
      </div>

      {/* Two-column layout: Main content + Sidebar */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Main Content Area */}
        <div className="lg:col-span-3 space-y-8">
          {/* Use shared ContentReviewDisplay component */}
          <ContentReviewDisplay
            submission={adaptedSubmission}
            submissionId={adaptedSubmission.id}
            showAssets={true}
            showSubtitles={true}
          />
        </div>

        {/* Sidebar: Admin Actions */}
        <div className="lg:col-span-1">
          <AdminActionsPanel
            submission={adaptedSubmission}
            onApprove={handleApprove}
            onReject={handleReject}
            onRefresh={handleRefresh}
            isRefreshing={isRefreshing}
            onTriggerEncoding={handleTriggerEncoding}
            isEncoding={encodingLoading}
          />
        </div>
      </div>

      {/* Approval Dialog */}
      <ApprovalDialog
        isOpen={showApprovalDialog}
        loading={approvalLoading}
        submissionTitle={adaptedSubmission.title}
        hasSubtitles={
          !!(
            (adaptedSubmission.featureSubtitles && adaptedSubmission.featureSubtitles.length > 0) ||
            (adaptedSubmission.trailerSubtitles && adaptedSubmission.trailerSubtitles.length > 0)
          )
        }
        onConfirm={(notes, publishImmediately, transferSubtitles) => {
          void handleApprovalConfirm(notes, publishImmediately, transferSubtitles);
        }}
        onCancel={handleApprovalCancel}
      />

      {/* Rejection Dialog */}
      <RejectionDialog
        isOpen={showRejectionDialog}
        loading={rejectionLoading}
        submissionTitle={adaptedSubmission.title}
        onConfirm={(reason, notes) => {
          void handleRejectionConfirm(reason, notes);
        }}
        onCancel={handleRejectionCancel}
      />
    </div>
  );
}
